import { Button, AnchorButton } from '@askable/ui/core/button';
import { cn } from '@askable/ui/lib/utils';
import { Menu, PanelLeft } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'src/lib/routing';
import { useSidebarCollapsed } from 'src/components/common/MainNavSidebar/hooks/useSidebarCollapsed';

import { EditableHeaderTitle } from './EditableHeaderTitle';

type BreadcrumbItem = {
  icon?: React.ElementType;
  title: string;
  href?: string; // If present, renders as a link; if not, renders as current page
  placeholder?: string; // Only relevant for the current page (last item)
  hideSeparator?: boolean; // Hide the trailing "/" separator (e.g. for a standalone back button)
};

type HeaderProps = {
  breadcrumbs: BreadcrumbItem[];
  children?: React.ReactNode;
  className?: string; // Override the surface when the page pane is not --canvas
  hasBorderBottom?: boolean;
  hasMobileNavigation?: boolean; // Show the sidebar navigation button on mobile. Default is true.
  hasThreeColumns?: boolean; // Center align the navigation in Studies pages
  isSaving?: boolean;
  isSticky?: boolean;
  onTitleChange?: (newTitle: string) => void; // For editing the last breadcrumb
  tag?: string; // Optional tag label displayed before the current page title
};

export const Header = ({
  breadcrumbs,
  children,
  className,
  hasBorderBottom = false,
  hasMobileNavigation = true,
  hasThreeColumns = false,
  isSaving = false,
  isSticky = false,
  onTitleChange,
  tag,
}: HeaderProps) => {
  const [, toggleCollapsed] = useSidebarCollapsed();
  const [isScrolled, setIsScrolled] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Show a bottom border when the header is sticky and scrolled
  useEffect(() => {
    if (!isSticky || !sentinelRef.current) {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      setIsScrolled(!entry.isIntersecting);
    });

    observer.observe(sentinelRef.current);

    return () => observer.disconnect();
  }, [isSticky]);

  const currentPage = breadcrumbs[breadcrumbs.length - 1];
  const parentBreadcrumbs = breadcrumbs.slice(0, -1);

  return (
    <>
      {isSticky ? <div ref={sentinelRef} className="h-0 w-full" /> : null}
      <header
        className={cn(
          // Matches the page pane (--canvas, on every route that uses this header)
          // and does not sit above it. The header is part of the surface, so a line
          // divides it, not a fill. The background is still necessary, because the
          // header is sticky and content scrolls below it.
          'w-full items-center justify-between gap-x-2 gap-y-1 bg-canvas px-3 py-2 focus-visible:outline-none md:px-4 md:pr-3',
          {
            'sticky top-0 z-10': isSticky,
            flex: !hasThreeColumns,
            'flex-col items-start sm:flex-row sm:items-center': parentBreadcrumbs.length > 1,
            'grid grid-cols-2 lg:grid-cols-3': hasThreeColumns,
            'border-b-0.5 border-border': (isSticky && isScrolled) || hasBorderBottom,
          },
          className,
        )}
      >
        <div className="flex min-w-40 flex-1 items-center">
          <div className="-ml-2 md:hidden">
            {hasMobileNavigation ? (
              <Button variant="ghost" size="icon" onClick={() => toggleCollapsed()}>
                <PanelLeft className="hidden size-4 md:block" />
                <Menu className="block size-4 md:hidden" />
              </Button>
            ) : null}
          </div>

          {parentBreadcrumbs.map((breadcrumb, index) => (
            <div key={breadcrumb.title} className="flex items-center">
              {breadcrumb.href ? (
                <AnchorButton
                  variant="ghost"
                  className={cn('p-2 md:-my-2', {
                    'md:-ml-2': index === 0,
                    'aspect-square md:aspect-auto': breadcrumb.icon,
                  })}
                  asChild
                >
                  <Link
                    to={breadcrumb.href}
                    className="min-w-20 text-sm text-muted-foreground"
                    title={breadcrumb.title}
                  >
                    {breadcrumb.icon ? <breadcrumb.icon className="size-4" /> : null}
                    <div
                      className={cn('max-w-40 truncate', {
                        'hidden md:inline-block': breadcrumb.icon,
                      })}
                    >
                      {breadcrumb.title}
                    </div>
                  </Link>
                </AnchorButton>
              ) : (
                <div className="flex items-center gap-1 px-2 text-sm text-muted-foreground">
                  {breadcrumb.icon ? <breadcrumb.icon className="size-4" /> : null}
                  <div className="hidden md:inline-block">{breadcrumb.title}</div>
                </div>
              )}
              {breadcrumb.hideSeparator ? null : <div className="text-icon-muted">/</div>}
            </div>
          ))}

          {tag ? (
            <div className="ml-1 hidden shrink-0 rounded-xl bg-secondary px-2 py-0.5 text-xs font-medium md:block">
              {tag}
            </div>
          ) : null}

          {currentPage && (currentPage.title !== '' || onTitleChange) ? (
            <h1 className="group min-w-20 flex-1 truncate text-foreground" title={currentPage.title}>
              {onTitleChange ? (
                <EditableHeaderTitle
                  title={currentPage.title}
                  icon={currentPage.icon}
                  placeholder={currentPage.placeholder}
                  onTitleChange={onTitleChange}
                  isSaving={isSaving}
                />
              ) : (
                <div
                  className={cn('flex h-8 flex-1 items-center gap-1 text-sm font-medium text-foreground', {
                    'pl-2': parentBreadcrumbs.length > 0,
                  })}
                >
                  {currentPage.icon ? <currentPage.icon className="size-4" /> : null}
                  <div className="truncate">{currentPage.title || currentPage.placeholder || ''}</div>
                </div>
              )}
            </h1>
          ) : null}
        </div>
        {children}
      </header>
    </>
  );
};
